import { createContext, useContext } from "react";
import type { NavigationType } from "react-router-dom";

/**
 * 라우트 엘리먼트 안에서 읽는 "실제" 이동 종류 (#461).
 *
 * Router 가 화면 전환을 위해 `<Routes location={location}>` 으로 렌더하므로, 그 아래에서 react-router 의
 * `useNavigationType()` 은 늘 "POP" 이다(7.x `useRoutes` 의 locationArg 분기 — eslint 가 import 자체를 막는다).
 * 실제 값은 Routes 밖의 전환 래퍼(`PageTransitionWrapper`)가 읽어 이 Context 로 내려준다(`RouteNavigationTypeBridge`).
 *
 * 읽는 규칙: 값은 "현재" 이동 종류라, 전환 애니메이션 중 아직 살아 있는 나가는 카드에도 바뀐 값이 간다.
 * 그래서 컴포넌트 최상위에서 읽은 값을 마운트 시점에 한 번 고정해 쓴다 — 예:
 *   const navType = useRouteNavigationType();
 *   const [entryNavType] = useState(navType); // 이후 렌더에서 navType 이 바뀌어도 entryNavType 은 마운트 때 값
 * (훅을 useState 초기화 콜백 안에서 부르지 않는다 — Rules of Hooks.)
 * 복원 판정("떠났던 항목으로 돌아왔는가")에는 이 값이 아니라 `location.key`(createEntryKeyedStore)를 쓴다.
 */
export const RouteNavigationTypeContext = createContext<NavigationType | null>(null);

/** Provider(Router 의 전환 래퍼) 밖에서 부르면 throw — 기본값을 POP 으로 두면 막으려는 오해를 조용히 돌려주기 때문. */
export const useRouteNavigationType = (): NavigationType => {
  const value = useContext(RouteNavigationTypeContext);
  if (value === null) {
    throw new Error(
      "useRouteNavigationType 은 Router 의 전환 래퍼(RouteNavigationTypeBridge) 안에서만 쓸 수 있습니다.",
    );
  }
  return value;
};
